import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { opportunitiesAPI, clientsAPI, usersAPI, commesseAPI } from '../lib/api';
import { ClientCombobox } from '../components/ClientCombobox';
import { CommessaCombobox } from '../components/CommessaCombobox';
import { ModelCombobox } from '../components/ModelCombobox';
import { MissingModelsDialog } from '../components/pipeline/MissingModelsDialog';
import { OpportunityDocuments } from '../components/OpportunityDocuments';
import { CardCover } from '../components/pipeline/CardCover';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
    Plus,
    MoreVertical,
    Pencil,
    Trash2,
    Euro,
    CalendarIcon,
    Building2,
    Loader2,
    GripVertical,
    Search,
    AlertTriangle,
    Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { apiErrorMessage } from '../lib/apiError';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { FileSpreadsheet, Download, Copy, ClipboardList, ArrowRight, FileText, Truck, Undo2, Flame, Hammer, Check, CheckCircle2, HelpCircle } from 'lucide-react';
import { QuoteForm, emptyQuote, computeQuote, serializeQuote } from '../components/pipeline/QuoteForm';import { QuoteHistory } from '../components/pipeline/QuoteHistory';
import { QuoteDiff } from '../components/pipeline/QuoteDiff';
import { buildSellerColors, initials } from '../components/pipeline/sellerColors';
import { useAuth } from '../context/AuthContext';

const STAGES = [
    { id: 'won', label: 'Offerte confermate', color: '#10B981' },
];

const PAYMENT_GROUPS = [
    { id: 'saldato', label: 'Saldate', color: '#10B981', match: (o) => o.payment_status === 'saldato' },
    { id: 'da_saldare', label: 'Da Saldare', color: '#EF4444', match: (o) => o.payment_status === 'da_saldare' },
    { id: 'confermate', label: 'Commesse Inserite', color: '#2563EB', match: (o) => !['saldato', 'da_saldare'].includes(o.payment_status) },
];

const STALE_PAYMENT_DAYS = 15;
const DELIVERY_SOON_DAYS = 7;

const daysSince = (dateStr) => {
    if (!dateStr) return 0;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return 0;
    return Math.floor((Date.now() - d.getTime()) / 86400000);
};

const startOfToday = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
};

const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return Math.round((d.getTime() - startOfToday().getTime()) / 86400000);
};

const isConfermata = (o) => !['saldato', 'da_saldare'].includes(o.payment_status);

const WelderIcon = ({ size = 12 }) => (
    <Hammer size={size} strokeWidth={2.5} aria-hidden="true" />
);

const FinishedIcon = ({ size = 12 }) => (
    <Check size={size} strokeWidth={3} aria-hidden="true" />
);


const isDelivered = (o) => {
    if ((o.kind || 'offerta') !== 'offerta') return false;
    if (o.delivered === true) return true;
    if (!o.delivery_date) return false;
    const d = new Date(o.delivery_date);
    if (isNaN(d.getTime())) return false;
    return d < startOfToday();
};

const isUpcomingDelivery = (o) => {
    if (isDelivered(o)) return false;
    if (!o.delivery_date) return false;
    const du = daysUntil(o.delivery_date);
    return du !== null && du >= 0 && du <= DELIVERY_SOON_DAYS;
};

const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    }).format(value);
};

const PipelinePage = ({ mode = 'offerte' }) => {
    const isPreventivi = mode === 'preventivi';
    const isConsegnati = mode === 'consegnati';
    const { user, isAdmin } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [opportunities, setOpportunities] = useState([]);
    const [missingModelsOpen, setMissingModelsOpen] = useState(false);
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);
    const [commesse, setCommesse] = useState([]);
    const [sellerFilter, setSellerFilter] = useState(null);
    const [yearFilter, setYearFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [photoFilter, setPhotoFilter] = useState('all');
    const [exporting, setExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editingOpportunity, setEditingOpportunity] = useState(null);
    const [detailsOpp, setDetailsOpp] = useState(null);
    const [activeTab, setActiveTab] = useState('details');
    const [hasQuote, setHasQuote] = useState(false);
    const [quote, setQuote] = useState(emptyQuote());
    const [formData, setFormData] = useState({
        title: '',
        client_id: '',
        value: '',
        stage: 'won',
        expected_close_date: null,
        description: '',
        delivery_date: null,
        production_date: null,
        payment_status: '',
        telaio: '',
        modello: '',
        commessa: '',
        commessa_id: '',
        assigned_to: '',
        in_produzione: false,
        finito: false
    });

    useEffect(() => {
        loadData();
    }, []);

    // Apri automaticamente il dettaglio offerta se arrivo dal calendario (?offer=<id>)
    useEffect(() => {
        const offerId = new URLSearchParams(location.search).get('offer');
        if (offerId && opportunities.length > 0) {
            const opp = opportunities.find(o => o.id === offerId);
            if (opp) {
                if (isConsegnati) setDetailsOpp(opp); else handleOpenDialog(opp);
                navigate(isConsegnati ? '/consegnati' : '/pipeline', { replace: true });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search, opportunities]);

    const loadData = async () => {
        try {
            const [oppsRes, clientsRes, usersRes] = await Promise.all([
                opportunitiesAPI.getAll(),
                clientsAPI.getAll(),
                usersAPI.getSellers()
            ]);
            setOpportunities(oppsRes.data);
            setClients(clientsRes.data);
            setUsers(usersRes.data);
            try {
                const commRes = await commesseAPI.getAll();
                setCommesse(commRes.data || []);
            } catch (e) { /* commesse opzionali */ }
        } catch (error) {
            toast.error('Errore nel caricamento dati');
        } finally {
            setLoading(false);
        }
    };

    const sellerColors = buildSellerColors(users);
    const getSellerColor = (userId) => sellerColors[userId] || '#6B7280';

    const clientNameById = useMemo(() => {
        const m = new Map();
        clients.forEach(c => m.set(c.id, c.company_name));
        return m;
    }, [clients]);

    // Selezione data (solo giorno, normalizzata a mezzogiorno per evitare slittamenti di fuso)
    const handleDateSelect = (field, date) => {
        if (!date) {
            setFormData(prev => ({ ...prev, [field]: null }));
            return;
        }
        const d = new Date(date);
        d.setHours(12, 0, 0, 0);
        setFormData(prev => ({ ...prev, [field]: d }));
    };

    const getUserName = (userId) => {
        if (userId === user?.id) return user.name;
        return users.find(u => u.id === userId)?.name || '';
    };

    const getClientName = (clientId) => {
        return clientNameById.get(clientId) || 'Cliente sconosciuto';
    };

    const activeSellers = users.filter(u => opportunities.some(o => o.assigned_to === u.id));
    const term = searchTerm.trim().toLowerCase();

    const oppYear = (o) => {
        const src = isConsegnati ? o.delivery_date : o.created_at;
        if (!src) return null;
        const d = new Date(src);
        return isNaN(d.getTime()) ? null : d.getFullYear();
    };
    const modeMatch = (o) => {
        const oppKind = o.kind || 'offerta';
        if (isPreventivi) return oppKind === 'preventivo';
        if (isConsegnati) return isDelivered(o);
        return oppKind !== 'preventivo' && !isDelivered(o);
    };
    const modeOpportunities = opportunities.filter(modeMatch);
    const availableYears = [...new Set(modeOpportunities.map(oppYear).filter(Boolean))].sort((a, b) => b - a);

    const visibleOpportunities = modeOpportunities.filter(o => {
        if (sellerFilter && o.assigned_to !== sellerFilter) return false;
        if (yearFilter !== 'all' && oppYear(o) !== Number(yearFilter)) return false;
        if (statusFilter === 'in_produzione' && !o.in_produzione) return false;
        if (statusFilter === 'finito' && !o.finito) return false;
        if (statusFilter === 'da_produrre' && (o.in_produzione || o.finito)) return false;
        if (statusFilter === 'da_saldare' && o.payment_status !== 'da_saldare') return false;
        if (statusFilter === 'saldato' && o.payment_status !== 'saldato') return false;
        if (isConsegnati && photoFilter === 'senza' && o.cover_thumb_url) return false;
        if (isConsegnati && photoFilter === 'con' && !o.cover_thumb_url) return false;
        if (!term) return true;
        return (
            (o.telaio || '').toLowerCase().includes(term) ||
            (o.modello || '').toLowerCase().includes(term) ||
            (o.commessa || '').toLowerCase().includes(term) ||
            (o.title || '').toLowerCase().includes(term) ||
            getClientName(o.client_id).toLowerCase().includes(term) ||
            (getUserName(o.assigned_to) || '').toLowerCase().includes(term)
        );
    });

    const boardGroups = isPreventivi
        ? [{ id: 'preventivo', label: 'Preventivi', color: '#2563EB', match: () => true }]
        : isConsegnati
            ? [{ id: 'consegnati', label: 'Offerte Consegnate', color: '#059669', match: () => true }]
            : PAYMENT_GROUPS;

    const confermateOpps = (isPreventivi || isConsegnati) ? [] : visibleOpportunities.filter(isConfermata);
    const confermateTotal = confermateOpps.reduce((sum, o) => sum + (o.value || 0), 0);
    const staleConfermate = confermateOpps.filter(o => daysSince(o.created_at) >= STALE_PAYMENT_DAYS);
    const upcomingDeliveries = (!isPreventivi && !isConsegnati) ? visibleOpportunities.filter(isUpcomingDelivery) : [];
    const missingModelOpps = !isPreventivi ? visibleOpportunities.filter(o => !(o.modello || '').trim()) : [];

    const resetForm = () => {
        setFormData({
            title: '',
            client_id: '',
            value: '',
            stage: 'won',
            expected_close_date: null,
            description: '',
            delivery_date: null,
            production_date: null,
            payment_status: '',
            telaio: '',
            modello: '',
            commessa: '',
            commessa_id: '',
            assigned_to: '',
            in_produzione: false,
            finito: false
        });
        setEditingOpportunity(null);
        setActiveTab('details');
        setHasQuote(false);
        setQuote(emptyQuote());
    };

    const toFormQuote = (q) => ({
        ...emptyQuote(),
        ...q,
        floors: q.floors ?? '',
        length: q.length ?? '',
        list_price: q.list_price ?? '',
        max_discount_price: q.max_discount_price ?? '',
        revision_date: q.revision_date || '',
        optionals: (q.optionals || []).map(o => ({ description: o.description, price: o.price })),
    });

    const handleOpenDialog = (opportunity = null, tab = 'details') => {
        if (opportunity) {
            setEditingOpportunity(opportunity);
            setFormData({
                title: opportunity.title,
                client_id: opportunity.client_id,
                value: opportunity.value.toString(),
                stage: opportunity.stage,
                expected_close_date: opportunity.expected_close_date ? new Date(opportunity.expected_close_date) : null,
                description: opportunity.description || '',
                delivery_date: opportunity.delivery_date ? new Date(opportunity.delivery_date) : null,
                production_date: opportunity.production_date ? new Date(opportunity.production_date) : null,
                payment_status: opportunity.payment_status || '',
                telaio: opportunity.telaio || '',
                modello: opportunity.modello || '',
                commessa: opportunity.commessa || '',
                commessa_id: opportunity.commessa_id || '',
                assigned_to: opportunity.assigned_to || '',
                in_produzione: !!opportunity.in_produzione,
                finito: !!opportunity.finito
            });
            setHasQuote(!!opportunity.quote || tab === 'quote');
            setQuote(opportunity.quote ? toFormQuote(opportunity.quote) : emptyQuote());
            setActiveTab(tab);
        } else {
            resetForm();
        }
        setIsDialogOpen(true);
        if (isAdmin() && !(opportunity && opportunity.commessa_id)) {
            fetchNextCommessa();
        }
    };

    const handleDownloadQuote = async (opportunity) => {
        try {
            const res = await opportunitiesAPI.downloadQuote(opportunity.id);
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${opportunity.quote?.number || 'preventivo'} - ${getClientName(opportunity.client_id)}.xlsx`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error('Errore nel download del preventivo');
        }
    };

    const handleDuplicate = async (opportunity) => {
        try {
            const res = await opportunitiesAPI.duplicate(opportunity.id);
            const label = res.data.quote?.number ? `Revisione creata: ${res.data.quote.number}` : `Offerta duplicata: ${res.data.title}`;
            toast.success(label);
            await loadData();
            handleOpenDialog(res.data, res.data.quote ? 'quote' : 'details');
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nella duplicazione'));
        }
    };

    const availableCommesse = useMemo(
        () => commesse,
        [commesse]
    );

    const fetchNextCommessa = async () => {
        try {
            const res = await commesseAPI.nextNumber();
            setFormData(f => f.commessa_id ? f : ({ ...f, commessa: res.data.number }));
        } catch { /* opzionale */ }
    };

    const onSelectCommessa = (id) => {
        if (!id) { setFormData(f => ({ ...f, commessa_id: '' })); return; }
        const com = commesse.find(c => c.id === id);
        if (com) setFormData(f => ({ ...f, commessa_id: id, telaio: com.telaio || '', commessa: com.number || '', modello: com.mod || f.modello }));
    };

    const handleCreateCommessaInline = async () => {
        if (!formData.telaio.trim()) { toast.error('Inserisci prima il numero di telaio'); return; }
        try {
            const res = await commesseAPI.create({
                telaio: formData.telaio.trim(),
                mod: formData.modello || undefined,
                number: formData.commessa?.trim() || undefined,
                client_id: formData.client_id || undefined,
                assigned_to: isAdmin() ? (formData.assigned_to || undefined) : undefined,
            });
            const com = res.data;
            setCommesse(prev => [com, ...prev]);
            setFormData(f => ({ ...f, commessa_id: com.id, commessa: com.number, telaio: com.telaio || f.telaio, modello: com.mod || f.modello }));
            toast.success(`Commessa creata: ${com.number}`);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Errore nella creazione commessa'));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();        if (!formData.client_id) {
            toast.error('Seleziona un cliente');
            setActiveTab('details');
            return;
        }
        try {
            const data = {
                ...formData,
                kind: isPreventivi ? 'preventivo' : 'offerta',
                value: hasQuote ? computeQuote(quote).total : (parseFloat(formData.value) || 0),
                expected_close_date: formData.expected_close_date?.toISOString(),
                delivery_date: formData.delivery_date?.toISOString(),
                clear_delivery_date: editingOpportunity && !formData.delivery_date ? true : undefined,
                production_date: formData.production_date?.toISOString(),
                clear_production_date: editingOpportunity && !formData.production_date ? true : undefined,
                payment_status: formData.payment_status || undefined,
                telaio: formData.telaio || undefined,
                modello: formData.modello || undefined,
                commessa: formData.commessa || undefined,
                commessa_id: formData.commessa_id || undefined,
                assigned_to: isAdmin() ? (formData.assigned_to || undefined) : undefined,
                in_produzione: !!formData.in_produzione,
                finito: !!formData.finito,
                quote: hasQuote ? serializeQuote(quote) : undefined
            };

            if (editingOpportunity) {
                await opportunitiesAPI.update(editingOpportunity.id, data);
                toast.success('Offerta aggiornata');
            } else {
                await opportunitiesAPI.create(data);
                toast.success('Offerta creata');
            }
            setIsDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel salvataggio'));
        }
    };

    const handleRemoveDeliveryDate = async (opp) => {
        if (!window.confirm(`Rimuovere la data di consegna di "${opp.title || 'questa commessa'}"? Tornerà tra le commesse attive e verrà tolta dalle Consegne.`)) return;
        try {
            await opportunitiesAPI.update(opp.id, { clear_delivery_date: true });
            toast.success('Data di consegna rimossa');
            setDetailsOpp(null);
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nella rimozione della data'));
        }
    };

    const handleTransformToOffer = async (opp) => {
        if (!window.confirm(`Trasformare il preventivo "${opp.title}" in offerta? Verrà spostato in Commesse Inserite.`)) return;
        try {
            await opportunitiesAPI.update(opp.id, { kind: 'offerta', payment_status: '' });
            toast.success('Preventivo trasformato in offerta');
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nella trasformazione'));
        }
    };

    const handleStageChange = async (opportunityId, newStage) => {
        try {
            await opportunitiesAPI.updateStage(opportunityId, newStage);
            toast.success('Stage aggiornato');
            loadData();
        } catch (error) {
            toast.error('Errore nell\'aggiornamento');
        }
    };

    const handleDelete = async (opportunityId) => {
        if (!window.confirm('Eliminare questo preventivo?')) return;
        
        try {
            await opportunitiesAPI.delete(opportunityId);
            toast.success('Offerta eliminata');
            loadData();
        } catch (error) {
            toast.error('Errore nell\'eliminazione');
        }
    };

    const getOpportunitiesByStage = (stageId) => {
        return visibleOpportunities.filter(o => o.stage === stageId);
    };

    const getStageTotal = (stageId) => {
        return getOpportunitiesByStage(stageId).reduce((sum, o) => sum + (o.value || 0), 0);
    };

    const getOpportunitiesByGroup = (group) => {
        const list = visibleOpportunities.filter(group.match);
        if (isConsegnati) {
            return [...list].sort((a, b) => new Date(b.delivery_date || 0) - new Date(a.delivery_date || 0));
        }
        return list;
    };

    const getGroupTotal = (group) => {
        return getOpportunitiesByGroup(group).reduce((sum, o) => sum + (o.value || 0), 0);
    };

    const handlePaymentChange = async (opportunityId, paymentStatus) => {
        try {
            await opportunitiesAPI.update(opportunityId, { payment_status: paymentStatus });
            toast.success('Stato pagamento aggiornato');
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore aggiornamento pagamento'));
        }
    };

    const handleToggleProduzione = async (opp) => {
        const next = !opp.in_produzione;
        try {
            await opportunitiesAPI.update(opp.id, { in_produzione: next, finito: next ? false : opp.finito });
            toast.success(next ? 'Offerta segnata in produzione' : 'Offerta tolta dalla produzione');
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore aggiornamento produzione'));
        }
    };

    const handleToggleFinito = async (opp) => {
        const next = !opp.finito;
        try {
            await opportunitiesAPI.update(opp.id, { finito: next, in_produzione: next ? false : opp.in_produzione });
            toast.success(next ? 'Commessa segnata come finita' : 'Commessa non più segnata come finita');
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore aggiornamento stato finito'));
        }
    };

    const handleMarkDelivered = async (opp) => {
        try {
            await opportunitiesAPI.update(opp.id, { delivered: true, in_produzione: false });
            toast.success('Offerta segnata come consegnata');
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel segnare come consegnata'));
        }
    };

    const handleUnmarkDelivered = async (opp) => {
        try {
            await opportunitiesAPI.update(opp.id, { delivered: false });
            const stillDelivered = opp.delivery_date && new Date(opp.delivery_date) < startOfToday();
            toast.success(stillDelivered
                ? 'Consegna manuale annullata (resta nei Consegnati: data già superata)'
                : 'Offerta riportata in Offerte');
            loadData();
        } catch (error) {
            toast.error(apiErrorMessage(error, 'Errore nel ripristino'));
        }
    };

    const handleExport = async (fmt) => {
        setExporting(true);
        try {
            const res = await opportunitiesAPI.export({
                fmt,
                mode: isPreventivi ? 'preventivi' : isConsegnati ? 'consegnati' : 'offerte',
                q: searchTerm,
                seller: sellerFilter || 'all',
                year: yearFilter,
            });
            const url = window.URL.createObjectURL(res.data);
            const a = document.createElement('a');
            const section = isPreventivi ? 'Preventivi' : isConsegnati ? 'Consegnati' : 'Offerte';
            const yr = yearFilter !== 'all' ? yearFilter : new Date().getFullYear();
            a.href = url;
            a.download = `${section}_${yr}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success(`Esportazione ${fmt.toUpperCase()} completata`);
        } catch (error) {
            toast.error('Errore nel download del file');
        } finally {
            setExporting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="pipeline-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl uppercase tracking-tight">{isPreventivi ? 'Preventivi' : isConsegnati ? 'Consegnati' : 'Commesse'}</h1>
                    <p className="text-muted-foreground">
                        {visibleOpportunities.length} {isPreventivi ? 'preventivi' : isConsegnati ? 'consegnate' : 'offerte'}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    {!isPreventivi && !isConsegnati && (
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[160px]" data-testid="status-filter">
                                <SelectValue placeholder="Stato" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti gli stati</SelectItem>
                                <SelectItem value="da_produrre" data-testid="status-da-produrre">Veicoli da produrre</SelectItem>
                                <SelectItem value="in_produzione" data-testid="status-in-produzione">In Produzione</SelectItem>
                                <SelectItem value="finito" data-testid="status-finito">Finito</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    {isConsegnati && (
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[160px]" data-testid="status-filter">
                                <SelectValue placeholder="Pagamento" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutti i pagamenti</SelectItem>
                                <SelectItem value="saldato" data-testid="status-saldato">Saldati</SelectItem>
                                <SelectItem value="da_saldare" data-testid="status-da-saldare">Da Saldare</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    {isConsegnati && (
                        <Select value={photoFilter} onValueChange={setPhotoFilter}>
                            <SelectTrigger className="w-[150px]" data-testid="photo-filter">
                                <SelectValue placeholder="Foto" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutte le foto</SelectItem>
                                <SelectItem value="senza" data-testid="photo-filter-senza">Senza foto</SelectItem>
                                <SelectItem value="con" data-testid="photo-filter-con">Con foto</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    <Select value={yearFilter} onValueChange={setYearFilter}>
                        <SelectTrigger className="w-[140px]" data-testid="year-filter">
                            <SelectValue placeholder="Anno" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tutti gli anni</SelectItem>
                            {availableYears.map(y => (
                                <SelectItem key={y} value={String(y)} data-testid={`year-option-${y}`}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={() => handleExport('xlsx')} disabled={exporting} data-testid="export-excel-btn">
                        {exporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileSpreadsheet size={16} className="mr-2" />} Excel
                    </Button>
                    <Button variant="outline" onClick={() => handleExport('pdf')} disabled={exporting} data-testid="export-pdf-btn">
                        {exporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileText size={16} className="mr-2" />} PDF
                    </Button>
                    {!isConsegnati && (
                    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button 
                            className="btn-primary font-heading"
                            onClick={() => handleOpenDialog()}
                            data-testid="add-opportunity-btn"
                        >
                            <Plus size={20} className="mr-2" />
                            {isPreventivi ? 'NUOVO PREVENTIVO' : 'NUOVA COMMESSA'}
                        </Button>
                    </DialogTrigger>
                    <DialogContent className={`${hasQuote ? 'max-w-3xl' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`} data-testid="opportunity-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-heading uppercase flex items-center gap-3">
                                {editingOpportunity ? (isPreventivi ? 'Modifica Preventivo' : 'Modifica Commessa') : (isPreventivi ? 'Nuovo Preventivo' : 'Nuova Commessa')}
                                {editingOpportunity && (
                                    <span
                                        className="inline-flex items-center gap-1.5 text-xs font-sans normal-case tracking-normal px-2 py-0.5 rounded-full text-white"
                                        style={{ backgroundColor: getSellerColor(editingOpportunity.assigned_to) }}
                                        data-testid="dialog-seller-badge"
                                    >
                                        {getUserName(editingOpportunity.assigned_to)}
                                    </span>
                                )}
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
                            <Tabs value={activeTab} onValueChange={setActiveTab}>
                                <TabsList className="grid w-full grid-cols-2">
                                    <TabsTrigger value="details" data-testid="tab-details">Dati Commessa</TabsTrigger>
                                    <TabsTrigger value="quote" data-testid="tab-quote">
                                        <FileSpreadsheet size={14} className="mr-1" /> Commessa
                                    </TabsTrigger>
                                </TabsList>
                                <TabsContent value="details" className="space-y-4 mt-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Titolo *</Label>
                                    <Input
                                        value={formData.title}
                                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                                        required
                                        data-testid="opportunity-title"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Cliente *</Label>
                                    <ClientCombobox
                                        clients={clients}
                                        value={formData.client_id}
                                        onChange={(value) => setFormData({...formData, client_id: value})}
                                        testid="opportunity-client"
                                    />
                                </div>
                                {isAdmin() && (
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Commerciale (chi ha venduto)</Label>
                                        <Select
                                            value={formData.assigned_to}
                                            onValueChange={(value) => setFormData({...formData, assigned_to: value})}
                                        >
                                            <SelectTrigger data-testid="opportunity-seller">
                                                <SelectValue placeholder="Assegna commerciale" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {users.filter(u => u.role !== 'addetto_consegna').map((u) => (
                                                    <SelectItem key={u.id} value={u.id}>
                                                        {u.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                )}
                                <div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Valore (€)</Label>
                                        <Input
                                            type="number"
                                            value={hasQuote ? computeQuote(quote).total : formData.value}
                                            onChange={(e) => setFormData({...formData, value: e.target.value})}
                                            disabled={hasQuote}
                                            data-testid="opportunity-value"
                                        />
                                        {hasQuote && <p className="text-xs text-muted-foreground mt-1">Calcolato dal preventivo</p>}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Data Offerta Confermata</Label>
                                    <div className="flex items-center h-10 px-4 border rounded-md bg-muted/40 text-sm text-muted-foreground cursor-not-allowed" title="Impostata automaticamente alla creazione della commessa" data-testid="opportunity-date">
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {formData.expected_close_date
                                            ? <span className="text-foreground">{format(formData.expected_close_date, 'PPP', { locale: it })}</span>
                                            : 'Automatica alla creazione della commessa'}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Data di Produzione</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start" data-testid="opportunity-production-date">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {formData.production_date
                                                    ? format(formData.production_date, 'PPP', { locale: it })
                                                    : 'Seleziona data'}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={formData.production_date}
                                                onSelect={(date) => handleDateSelect('production_date', date)}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    {formData.production_date && (
                                        <div className="flex justify-end mt-1">
                                            <button
                                                type="button"
                                                onClick={() => handleDateSelect('production_date', null)}
                                                className="text-xs text-red-600 hover:underline whitespace-nowrap"
                                                data-testid="clear-production-date"
                                            >
                                                ✕ Rimuovi data
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider font-bold text-red-600">Data di Consegna</Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button variant="outline" className="w-full justify-start" data-testid="opportunity-delivery-date">
                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                {formData.delivery_date 
                                                    ? format(formData.delivery_date, 'PPP', { locale: it })
                                                    : 'Seleziona data'}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0">
                                            <Calendar
                                                mode="single"
                                                selected={formData.delivery_date}
                                                onSelect={(date) => handleDateSelect('delivery_date', date)}
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    {formData.delivery_date && (
                                        <div className="flex items-center justify-between mt-1">
                                            <p className="text-xs text-muted-foreground">
                                                Verrà aggiunta automaticamente al calendario come consegna
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => handleDateSelect('delivery_date', null)}
                                                className="text-xs text-red-600 hover:underline whitespace-nowrap ml-2"
                                                data-testid="clear-delivery-date"
                                            >
                                                ✕ Rimuovi data
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Modello veicolo</Label>
                                    <ModelCombobox
                                        value={formData.modello}
                                        onChange={(modello) => setFormData({ ...formData, modello })}
                                        testid="opportunity-modello"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Pagamento</Label>
                                        <Select
                                            value={formData.payment_status}
                                            onValueChange={(value) => setFormData({...formData, payment_status: value})}
                                        >
                                            <SelectTrigger data-testid="opportunity-payment">
                                                <SelectValue placeholder="Seleziona" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="da_saldare">Da Saldare</SelectItem>
                                                <SelectItem value="acconto">Acconto</SelectItem>
                                                <SelectItem value="saldato">Saldato</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {isAdmin() && (
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider">Telaio</Label>
                                        <Input
                                            value={formData.telaio}
                                            onChange={(e) => setFormData({...formData, telaio: e.target.value})}
                                            disabled={!!formData.commessa_id}
                                            placeholder="ZFJSBA..."
                                            data-testid="opportunity-telaio"
                                        />
                                    </div>
                                    )}
                                </div>
                                {!isPreventivi && (
                                <label
                                    className="flex items-center gap-3 rounded-sm border p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                                    data-testid="opportunity-produzione-label"
                                >
                                    <Checkbox
                                        checked={!!formData.in_produzione}
                                        onCheckedChange={(v) => setFormData({ ...formData, in_produzione: !!v, finito: v ? false : formData.finito })}
                                        data-testid="opportunity-produzione"
                                    />
                                    <span className="flex items-center gap-2 text-sm font-medium">
                                        <span className="text-blue-600"><WelderIcon size={18} /></span>
                                        In Produzione
                                    </span>
                                </label>
                                )}
                                {!isPreventivi && (
                                <label
                                    className="flex items-center gap-3 rounded-sm border p-3 cursor-pointer hover:bg-muted/40 transition-colors"
                                    data-testid="opportunity-finito-label"
                                >
                                    <Checkbox
                                        checked={!!formData.finito}
                                        onCheckedChange={(v) => setFormData({ ...formData, finito: !!v, in_produzione: v ? false : formData.in_produzione })}
                                        data-testid="opportunity-finito"
                                    />
                                    <span className="flex items-center gap-2 text-sm font-medium">
                                        <span className="text-green-600"><FinishedIcon size={18} /></span>
                                        Finito
                                    </span>
                                </label>
                                )}
                                {/* Commessa: solo gli admin gestiscono telaio e commesse */}
                                {isAdmin() && (
                                <div className="rounded-sm border p-3 space-y-2 bg-muted/20">
                                    <Label className="text-xs uppercase tracking-wider">Commessa</Label>
                                    <div className="flex gap-2">
                                        <div className="flex-1">
                                            <CommessaCombobox
                                                commesse={availableCommesse}
                                                value={formData.commessa_id}
                                                onChange={onSelectCommessa}
                                                testid="opportunity-commessa-select"
                                            />
                                        </div>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleCreateCommessaInline}
                                            data-testid="opportunity-commessa-new"
                                            title="Crea una nuova commessa dal telaio inserito"
                                        >
                                            <Plus size={16} className="mr-1" /> Nuova
                                        </Button>
                                    </div>
                                    {formData.commessa_id ? (
                                        <p className="text-xs text-muted-foreground" data-testid="opportunity-commessa-linked">
                                            Commessa collegata: <b className="text-foreground">{formData.commessa}</b> · Telaio <b className="text-foreground">{formData.telaio || '—'}</b>
                                        </p>
                                    ) : (
                                        <div>
                                            <Input
                                                value={formData.commessa}
                                                onChange={(e) => setFormData({...formData, commessa: e.target.value})}
                                                placeholder="N. commessa successiva libera"
                                                data-testid="opportunity-commessa"
                                            />
                                            <p className="text-[11px] text-muted-foreground mt-1">
                                                Viene proposta automaticamente la <b>prima commessa libera</b> (successiva all'ultima creata). Inserisci il Telaio qui sopra, eventualmente modifica il numero, poi premi "Nuova" per crearla.
                                            </p>
                                        </div>
                                    )}
                                </div>
                                )}
                                {!isAdmin() && (formData.commessa || formData.telaio) && (
                                    <div className="rounded-sm border p-3 bg-muted/20" data-testid="opportunity-commessa-readonly">
                                        <Label className="text-xs uppercase tracking-wider">Commessa</Label>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Commessa: <b className="text-foreground">{formData.commessa || '—'}</b> · Telaio <b className="text-foreground">{formData.telaio || '—'}</b>
                                        </p>
                                        <p className="text-[11px] text-muted-foreground mt-1">Gestita dall'amministratore.</p>
                                    </div>
                                )}
                                <div>
                                    <Label className="text-xs uppercase tracking-wider">Descrizione</Label>
                                    <Textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        rows={3}
                                        data-testid="opportunity-description"
                                    />
                                </div>
                                <OpportunityDocuments opportunityId={editingOpportunity?.id} onChanged={loadData} />
                                </TabsContent>
                                <TabsContent value="quote" className="mt-4 space-y-4">
                                    <div className="flex items-center justify-between rounded-sm border px-4 py-3">
                                        <div>
                                            <p className="font-medium">Compila preventivo</p>
                                            <p className="text-xs text-muted-foreground">Foglio offerta con optional, totali e verifica sconto</p>
                                        </div>
                                        <Switch checked={hasQuote} onCheckedChange={setHasQuote} data-testid="quote-toggle" />
                                    </div>
                                    {hasQuote && (
                                        <QuoteForm
                                            quote={quote}
                                            onChange={setQuote}
                                            sellerName={editingOpportunity ? getUserName(editingOpportunity.assigned_to) : user?.name}
                                            clientName={formData.client_id ? getClientName(formData.client_id) : ''}
                                        />
                                    )}
                                    {hasQuote && editingOpportunity?.quote?.parent_id && (
                                        <QuoteDiff
                                            prev={opportunities.find(o => o.id === editingOpportunity.quote.parent_id)}
                                            current={quote}
                                        />
                                    )}
                                    <QuoteHistory
                                        opportunity={editingOpportunity}
                                        opportunities={opportunities}
                                        onOpen={(o) => handleOpenDialog(o, 'quote')}
                                    />
                                </TabsContent>
                            </Tabs>
                            <div className="flex flex-wrap justify-end gap-3 pt-3 sticky bottom-0 bg-background border-t -mx-1 px-1 pb-1 mt-2">
                                {editingOpportunity && (
                                    <Button type="button" variant="outline" onClick={() => handleDuplicate(editingOpportunity)} data-testid="quote-duplicate-btn">
                                        <Copy size={16} className="mr-2" /> Duplica
                                    </Button>
                                )}
                                {editingOpportunity?.quote && (
                                    <Button type="button" variant="outline" onClick={() => handleDownloadQuote(editingOpportunity)} data-testid="quote-download-btn">
                                        <Download size={16} className="mr-2" /> Scarica Excel
                                    </Button>
                                )}
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                                    Annulla
                                </Button>
                                <Button type="submit" className="btn-primary" data-testid="opportunity-submit">
                                    {editingOpportunity ? 'AGGIORNA' : 'CREA'}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
                    )}
                </div>
            </div>

            {/* Dialog Dettagli Consegna (sola lettura + allegati) */}
            <Dialog open={!!detailsOpp} onOpenChange={(v) => !v && setDetailsOpp(null)}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="delivered-details-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading uppercase">Dettagli consegna</DialogTitle>
                    </DialogHeader>
                    {detailsOpp && (
                        <div className="space-y-3 mt-2 text-sm">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Titolo</p>
                                    <p className="font-medium">{detailsOpp.title || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Cliente</p>
                                    <p className="font-medium">{getClientName(detailsOpp.client_id)}</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Commerciale</p>
                                    <p className="font-medium">{getUserName(detailsOpp.assigned_to) || '—'}</p>
                                </div>
                                <div>
                                    <p className="text-xs uppercase tracking-wider text-muted-foreground">Valore</p>
                                    <p className="font-medium">{formatCurrency(detailsOpp.value || 0)}</p>
                                </div>
                                {detailsOpp.commessa && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Commessa</p>
                                        <p className="font-medium font-mono">{detailsOpp.commessa}</p>
                                    </div>
                                )}
                                {detailsOpp.telaio && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Telaio</p>
                                        <p className="font-medium font-mono">{detailsOpp.telaio}</p>
                                    </div>
                                )}
                                {detailsOpp.modello && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Modello</p>
                                        <p className="font-medium" data-testid="details-modello">{detailsOpp.modello}</p>
                                    </div>
                                )}
                                {detailsOpp.production_date && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Data produzione</p>
                                        <p className="font-medium" data-testid="details-production-date">{format(new Date(detailsOpp.production_date), 'PPP', { locale: it })}</p>
                                    </div>
                                )}
                                {detailsOpp.delivery_date && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Data consegna</p>
                                        <p className="font-medium">{format(new Date(detailsOpp.delivery_date), 'PPP', { locale: it })}</p>
                                    </div>
                                )}
                                {detailsOpp.quote?.number && (
                                    <div>
                                        <p className="text-xs uppercase tracking-wider text-muted-foreground">Preventivo</p>
                                        <p className="font-medium font-mono">{detailsOpp.quote.number}</p>
                                    </div>
                                )}
                            </div>
                            <OpportunityDocuments opportunityId={detailsOpp.id} onChanged={loadData} />
                            {isConsegnati && detailsOpp.delivery_date && (isAdmin() || user?.role === 'backoffice') && (
                                <div className="pt-3 border-t">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                                        onClick={() => handleRemoveDeliveryDate(detailsOpp)}
                                        data-testid="remove-delivery-date-btn"
                                    >
                                        ✕ Rimuovi data di consegna
                                    </Button>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        La commessa tornerà tra quelle attive e sarà rimossa dalle Consegne.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Seller legend / filter */}
            {activeSellers.length > 0 && (
                <div className="flex flex-wrap items-center gap-2" data-testid="seller-legend">
                    <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Commerciali:</span>
                    {activeSellers.map((s) => {
                        const active = sellerFilter === s.id;
                        const count = opportunities.filter(o => o.assigned_to === s.id).length;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => setSellerFilter(active ? null : s.id)}
                                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs transition-all ${active ? 'text-white shadow-sm' : 'bg-background hover:bg-muted'} ${sellerFilter && !active ? 'opacity-50' : ''}`}
                                style={active ? { backgroundColor: getSellerColor(s.id), borderColor: getSellerColor(s.id) } : { borderColor: getSellerColor(s.id) }}
                                data-testid={`seller-filter-${s.id}`}
                            >
                                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: active ? '#fff' : getSellerColor(s.id) }} />
                                {s.name}
                                <span className={`font-mono ${active ? 'text-white/80' : 'text-muted-foreground'}`}>{count}</span>
                            </button>
                        );
                    })}
                    {sellerFilter && (
                        <button type="button" onClick={() => setSellerFilter(null)} className="text-xs underline text-muted-foreground ml-1" data-testid="seller-filter-clear">
                            Mostra tutti
                        </button>
                    )}
                </div>
            )}

            {/* Search bar */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Cerca per telaio, commessa, cliente o commerciale..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="opportunity-search"
                />
                {searchTerm && (
                    <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="opportunity-search-clear"
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Commesse senza modello */}
            {!isPreventivi && missingModelOpps.length > 0 && (
                <div
                    className="rounded-sm border-l-4 border-amber-500 bg-amber-50/70 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2"
                    data-testid="missing-models-banner"
                >
                    <HelpCircle size={16} className="text-amber-600" />
                    <span className="text-sm">
                        <b>{missingModelOpps.length}</b> {missingModelOpps.length === 1 ? 'commessa senza modello veicolo' : 'commesse senza modello veicolo'}
                    </span>
                    <Button size="sm" variant="outline" className="ml-auto h-8" onClick={() => setMissingModelsOpen(true)} data-testid="missing-models-open">
                        Compila modelli
                    </Button>
                </div>
            )}
            <MissingModelsDialog
                open={missingModelsOpen}
                onOpenChange={setMissingModelsOpen}
                opportunities={missingModelOpps}
                getClientName={getClientName}
                onSaved={loadData}
            />

            {/* Promemoria consegne imminenti */}
            {!isPreventivi && !isConsegnati && upcomingDeliveries.length > 0 && (
                <div
                    className="rounded-sm border-l-4 bg-amber-50/70 dark:bg-amber-950/20 px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2"
                    style={{ borderLeftColor: '#F59E0B' }}
                    data-testid="upcoming-delivery-summary"
                >
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-500">
                        <Truck size={16} />
                        <span className="text-xs uppercase tracking-wider font-medium">
                            {upcomingDeliveries.length} {upcomingDeliveries.length === 1 ? 'consegna prevista' : 'consegne previste'} nei prossimi {DELIVERY_SOON_DAYS} giorni
                        </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {upcomingDeliveries
                            .slice()
                            .sort((a, b) => daysUntil(a.delivery_date) - daysUntil(b.delivery_date))
                            .slice(0, 6)
                            .map(o => {
                                const du = daysUntil(o.delivery_date);
                                return (
                                    <span key={o.id} className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border border-amber-200" data-testid={`upcoming-chip-${o.id}`}>
                                        {o.title} · {du === 0 ? 'oggi' : `${du}g`}
                                    </span>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* Colonne per stato pagamento */}
            <div className="pb-4">
                <div className={`grid grid-cols-1 ${(isPreventivi || isConsegnati) ? 'lg:grid-cols-1 max-w-2xl' : 'lg:grid-cols-3'} gap-4 items-start`}>
                    {boardGroups.map((group) => (
                        <div
                            key={group.id}
                            className="w-full bg-muted/30 rounded-sm"
                            data-testid={`payment-group-${group.id}`}
                        >
                            {/* Column Header */}
                            <div 
                                className="p-4 border-b"
                                style={{ borderBottomColor: group.color, borderBottomWidth: '3px' }}
                            >
                                <div className="flex items-center justify-between">
                                    <h3 className="font-heading uppercase text-sm tracking-wider">
                                        {group.label}
                                    </h3>
                                    <Badge variant="secondary" className="text-xs" data-testid={`payment-group-count-${group.id}`}>
                                        {getOpportunitiesByGroup(group).length}
                                    </Badge>
                                </div>
                                <p className="text-sm font-mono mt-1 text-muted-foreground" data-testid={`payment-group-total-${group.id}`}>
                                    {formatCurrency(getGroupTotal(group))}
                                </p>
                            </div>

                            {/* Cards */}
                            <div className="p-3 grid grid-cols-1 gap-3 min-h-[200px] items-start">
                                {getOpportunitiesByGroup(group).map((opportunity) => (
                                    <Card 
                                        key={opportunity.id} 
                                        className="kanban-card group cursor-pointer border-l-4"
                                        style={{ borderLeftColor: getSellerColor(opportunity.assigned_to) }}
                                        data-testid={`opportunity-card-${opportunity.id}`}
                                    >
                                        <CardContent className="p-3 flex gap-3">
                                            {!isPreventivi && <CardCover opportunity={opportunity} onChanged={loadData} />}
                                            <div className="flex-1 min-w-0">
                                            {/* Riga compatta - sempre visibile */}
                                            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2">
                                                <span
                                                    className="h-6 w-6 rounded-full text-white text-[10px] font-semibold flex items-center justify-center shrink-0"
                                                    style={{ backgroundColor: getSellerColor(opportunity.assigned_to) }}
                                                    title={getUserName(opportunity.assigned_to)}
                                                    data-testid={`card-seller-${opportunity.id}`}
                                                >
                                                    {initials(getUserName(opportunity.assigned_to))}
                                                </span>
                                                <h4 className="font-medium text-sm truncate flex-1 min-w-[8rem] sm:min-w-0">
                                                    {opportunity.title}
                                                </h4>
                                                {!isPreventivi && opportunity.payment_status && (() => {
                                                    const ps = opportunity.payment_status;
                                                    const cfg = ps === 'saldato'
                                                        ? { bg: '#10B981', label: 'Saldato' }
                                                        : ps === 'acconto'
                                                            ? { bg: '#F59E0B', label: 'Acconto' }
                                                            : { bg: '#EF4444', label: 'Da Saldare' };
                                                    return (
                                                        <span
                                                            className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                                                            style={{ backgroundColor: cfg.bg }}
                                                            title={cfg.label}
                                                            data-testid={`card-payment-${opportunity.id}`}
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full bg-white" />
                                                            {cfg.label}
                                                        </span>
                                                    );
                                                })()}
                                                {!isPreventivi && !(opportunity.modello || '').trim() && (
                                                    <span
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-300"
                                                        title="Modello veicolo non indicato"
                                                        data-testid={`card-no-model-${opportunity.id}`}
                                                    >
                                                        <HelpCircle size={11} /> Modello?
                                                    </span>
                                                )}
                                                {!isPreventivi && !isConsegnati && opportunity.in_produzione && !isDelivered(opportunity) && (
                                                    <span
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                                                        style={{ backgroundColor: '#2563EB' }}
                                                        title="In produzione"
                                                        data-testid={`card-produzione-${opportunity.id}`}
                                                    >
                                                        <WelderIcon size={13} />
                                                        Prod
                                                    </span>
                                                )}
                                                {!isPreventivi && !isConsegnati && opportunity.finito && !isDelivered(opportunity) && (
                                                    <span
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                                                        style={{ backgroundColor: '#16A34A' }}
                                                        title="Finito"
                                                        data-testid={`card-finito-${opportunity.id}`}
                                                    >
                                                        <FinishedIcon size={13} />
                                                        Finito
                                                    </span>
                                                )}
                                                {!isPreventivi && isConfermata(opportunity) && daysSince(opportunity.created_at) >= STALE_PAYMENT_DAYS && (
                                                    <span
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                                                        style={{ backgroundColor: '#F59E0B' }}
                                                        title={`In attesa di pagamento da ${daysSince(opportunity.created_at)} giorni`}
                                                        data-testid={`card-stale-${opportunity.id}`}
                                                    >
                                                        <AlertTriangle size={11} />
                                                        {daysSince(opportunity.created_at)}g
                                                    </span>
                                                )}
                                                {!isPreventivi && !isConsegnati && isUpcomingDelivery(opportunity) && (() => {
                                                    const du = daysUntil(opportunity.delivery_date);
                                                    return (
                                                        <span
                                                            className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                                                            style={{ backgroundColor: '#EA580C' }}
                                                            title={`Consegna prevista ${du === 0 ? 'oggi' : `tra ${du} giorni`} (${format(new Date(opportunity.delivery_date), 'PPP', { locale: it })})`}
                                                            data-testid={`card-upcoming-${opportunity.id}`}
                                                        >
                                                            <Truck size={11} />
                                                            {du === 0 ? 'oggi' : `${du}g`}
                                                        </span>
                                                    );
                                                })()}
                                                {isPreventivi && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        className="h-7 px-2 shrink-0 bg-primary text-white text-[11px]"
                                                        onClick={(e) => { e.stopPropagation(); handleTransformToOffer(opportunity); }}
                                                        data-testid={`card-transform-${opportunity.id}`}
                                                        title="Trasforma in Commessa"
                                                    >
                                                        <ArrowRight size={13} className="mr-1" /> In Commesse
                                                    </Button>
                                                )}
                                                {isConsegnati && opportunity.delivery_date && (
                                                    <span
                                                        className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full text-white"
                                                        style={{ backgroundColor: '#059669' }}
                                                        title={`Consegnata il ${format(new Date(opportunity.delivery_date), 'PPP', { locale: it })}`}
                                                        data-testid={`card-delivered-${opportunity.id}`}
                                                    >
                                                        <CalendarIcon size={11} />
                                                        {format(new Date(opportunity.delivery_date), 'dd/MM/yy', { locale: it })}
                                                    </span>
                                                )}
                                                <span className="flex items-center gap-1 text-sm font-mono whitespace-nowrap">
                                                    <Euro size={12} />
                                                    {formatCurrency(opportunity.value || 0)}
                                                </span>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={(e) => e.stopPropagation()} data-testid={`card-menu-${opportunity.id}`}>
                                                            <MoreVertical size={16} />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        {isConsegnati && (
                                                            <DropdownMenuItem onClick={() => setDetailsOpp(opportunity)} data-testid={`menu-details-${opportunity.id}`}>
                                                                <Eye size={14} className="mr-2" />
                                                                Dettagli e allegati
                                                            </DropdownMenuItem>
                                                        )}
                                                        {!isConsegnati && (
                                                        <DropdownMenuItem onClick={() => handleOpenDialog(opportunity)}>
                                                            <Pencil size={14} className="mr-2" />
                                                            Modifica
                                                        </DropdownMenuItem>
                                                        )}
                                                        {!isConsegnati && (
                                                        <DropdownMenuItem onClick={() => handleOpenDialog(opportunity, 'quote')} data-testid={`menu-quote-${opportunity.id}`}>
                                                            <FileSpreadsheet size={14} className="mr-2" />
                                                            {opportunity.quote ? 'Apri preventivo' : 'Crea preventivo'}
                                                        </DropdownMenuItem>
                                                        )}
                                                        {opportunity.quote && (
                                                            <DropdownMenuItem onClick={() => handleDownloadQuote(opportunity)} data-testid={`menu-download-${opportunity.id}`}>
                                                                <Download size={14} className="mr-2" />
                                                                Scarica Excel
                                                            </DropdownMenuItem>
                                                        )}
                                                        {!isConsegnati && (
                                                        <DropdownMenuItem onClick={() => handleDuplicate(opportunity)} data-testid={`menu-duplicate-${opportunity.id}`}>
                                                            <Copy size={14} className="mr-2" />
                                                            {opportunity.quote ? 'Duplica preventivo' : 'Duplica offerta'}
                                                        </DropdownMenuItem>
                                                        )}
                                                        {isPreventivi ? (
                                                            <DropdownMenuItem onClick={() => handleTransformToOffer(opportunity)} data-testid={`menu-transform-${opportunity.id}`}>
                                                                <FileSpreadsheet size={14} className="mr-2" />
                                                                Trasforma in Offerta
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            <>
                                                                {PAYMENT_GROUPS.filter(g => isConsegnati ? !g.match(opportunity) : g.id !== group.id).map((g) => (
                                                                    <DropdownMenuItem 
                                                                        key={g.id}
                                                                        onClick={() => handlePaymentChange(opportunity.id, g.id === 'confermate' ? '' : g.id)}
                                                                        data-testid={`menu-move-${g.id}-${opportunity.id}`}
                                                                    >
                                                                        {isConsegnati
                                                                            ? ({ saldato: 'Segna come saldato', da_saldare: 'Segna da saldare', confermate: 'Togli stato pagamento' }[g.id])
                                                                            : `Sposta in ${g.label}`}
                                                                    </DropdownMenuItem>
                                                                ))}
                                                                {opportunity.payment_status !== 'acconto' && (
                                                                    <DropdownMenuItem onClick={() => handlePaymentChange(opportunity.id, 'acconto')} data-testid={`menu-move-acconto-${opportunity.id}`}>
                                                                        Segna in acconto
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {!isConsegnati && (
                                                                    <DropdownMenuItem onClick={() => handleToggleProduzione(opportunity)} data-testid={`menu-toggle-produzione-${opportunity.id}`}>
                                                                        <Flame size={14} className="mr-2" />
                                                                        {opportunity.in_produzione ? 'Togli da produzione' : 'Segna in produzione'}
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {!isConsegnati && (
                                                                    <DropdownMenuItem onClick={() => handleToggleFinito(opportunity)} data-testid={`menu-toggle-finito-${opportunity.id}`}>
                                                                        <CheckCircle2 size={14} className="mr-2" />
                                                                        {opportunity.finito ? 'Togli da finito' : 'Segna come finito'}
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {isConsegnati ? (
                                                                    <DropdownMenuItem onClick={() => handleUnmarkDelivered(opportunity)} data-testid={`menu-unmark-delivered-${opportunity.id}`}>
                                                                        <Undo2 size={14} className="mr-2" />
                                                                        Riporta in Commesse
                                                                    </DropdownMenuItem>
                                                                ) : (
                                                                    <DropdownMenuItem onClick={() => handleMarkDelivered(opportunity)} data-testid={`menu-mark-delivered-${opportunity.id}`}>
                                                                        <Truck size={14} className="mr-2" />
                                                                        Segna come consegnata
                                                                    </DropdownMenuItem>
                                                                )}
                                                            </>
                                                        )}
                                                        <DropdownMenuItem 
                                                            onClick={() => handleDelete(opportunity.id)}
                                                            className="text-destructive"
                                                        >
                                                            <Trash2 size={14} className="mr-2" />
                                                            Elimina
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>

                                            {/* Riga sempre visibile: commessa · telaio · cliente · modello */}
                                            <div
                                                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 pl-8 text-[11px] text-muted-foreground"
                                                data-testid={`card-preview-${opportunity.id}`}
                                            >
                                                {opportunity.commessa && (
                                                    <span className="font-mono"><span className="font-medium text-foreground">Comm.</span> {opportunity.commessa}</span>
                                                )}
                                                {opportunity.telaio && (
                                                    <span className="font-mono"><span className="font-medium text-foreground">Telaio</span> {opportunity.telaio}</span>
                                                )}
                                                <span className="truncate max-w-[220px]">{getClientName(opportunity.client_id)}</span>
                                                {opportunity.modello && (
                                                    <span className="truncate max-w-[260px] font-medium text-foreground" data-testid={`card-preview-modello-${opportunity.id}`}>{opportunity.modello}</span>
                                                )}
                                            </div>

                                            {/* Dettagli espandibili al passaggio del mouse */}
                                            <div className="grid grid-rows-[0fr] overflow-hidden transition-all duration-300 group-hover:grid-rows-[1fr] group-focus-within:grid-rows-[1fr]">
                                                <div className="min-h-0">
                                                    <div className="mt-3 pt-3 border-t">
                                                        <div className="min-w-0">
                                                            <div className="flex-1 min-w-0">
                                                                <span className="text-xs font-medium" style={{ color: getSellerColor(opportunity.assigned_to) }}>
                                                                    {getUserName(opportunity.assigned_to) || 'Commerciale'}
                                                                </span>
                                                                {opportunity.quote?.number && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleOpenDialog(opportunity, 'quote')}
                                                                        className="flex items-center gap-1 text-[11px] font-mono mt-1 px-1.5 py-0.5 rounded-sm bg-muted hover:bg-muted/70 transition-colors"
                                                                        data-testid={`quote-badge-${opportunity.id}`}
                                                                    >
                                                                        <FileSpreadsheet size={11} /> {opportunity.quote.number}
                                                                    </button>
                                                                )}
                                                                <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                                                                    <Building2 size={12} />
                                                                    <span className="truncate">
                                                                        {getClientName(opportunity.client_id)}
                                                                    </span>
                                                                </div>
                                                                {opportunity.commessa && (
                                                                    opportunity.commessa_id ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={(e) => { e.stopPropagation(); navigate(`/commesse?q=${encodeURIComponent(opportunity.commessa)}`); }}
                                                                            className="relative z-10 inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200 text-xs font-medium hover:bg-blue-100 transition-colors"
                                                                            title="Apri la commessa collegata"
                                                                            data-testid={`card-commessa-badge-${opportunity.id}`}
                                                                        >
                                                                            <ClipboardList size={11} /> Commessa {opportunity.commessa}
                                                                        </button>
                                                                    ) : (
                                                                        <div className="text-xs text-muted-foreground mt-1" data-testid={`card-commessa-${opportunity.id}`}>
                                                                            <span className="font-medium text-foreground">Commessa:</span> {opportunity.commessa}
                                                                        </div>
                                                                    )
                                                                )}
                                                                {opportunity.telaio && (
                                                                    <div className="text-xs text-muted-foreground mt-0.5" data-testid={`card-telaio-${opportunity.id}`}>
                                                                        <span className="font-medium text-foreground">Telaio:</span> {opportunity.telaio}
                                                                    </div>
                                                                )}
                                                                {opportunity.modello && (
                                                                    <div className="text-xs text-muted-foreground mt-0.5 truncate" data-testid={`card-modello-${opportunity.id}`}>
                                                                        <span className="font-medium text-foreground">Modello:</span> {opportunity.modello}
                                                                    </div>
                                                                )}
                                                                {opportunity.production_date && (
                                                                    <div className="text-xs text-amber-700 mt-0.5" data-testid={`card-production-${opportunity.id}`}>
                                                                        <span className="font-medium">Produzione:</span> {format(new Date(opportunity.production_date), 'dd/MM/yyyy')}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {opportunity.expected_close_date && (
                                                            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
                                                                <CalendarIcon size={12} />
                                                                {format(new Date(opportunity.expected_close_date), 'dd MMM', { locale: it })}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}

                                {getOpportunitiesByGroup(group).length === 0 && (
                                    <div className="text-center py-8 text-muted-foreground text-sm">
                                        {isPreventivi ? 'Nessun preventivo' : isConsegnati ? 'Nessuna offerta consegnata' : 'Nessuna offerta'}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PipelinePage;
